// MARK:- Homepage Shop the Look component: selection, tooltips and cart actions
if (!customElements.get('tpm-shop-the-look')) {
  customElements.define('tpm-shop-the-look', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.tabs = this.querySelector('[data-tpm-look-tabs]');
      this.panels = this.querySelector('[data-tpm-look-panels]');
      this.status = this.querySelector('[data-tpm-look-status]');
      this.announcement = this.querySelector('[data-tpm-look-announcement]');
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.addEventListener('pointerover', event => this.onTooltipPointer(event), options);
      this.addEventListener('pointerout', event => this.onTooltipPointer(event), options);
      this.addEventListener('focusin', event => {
        const button = event.target.closest('[data-hotspot-product-id]');
        if (button && button.matches(':focus-visible')) this.openTooltip(button);
      }, options);
      this.addEventListener('focusout', event => {
        if (event.target === this.tooltipButton) this.closeTooltip();
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.closeTooltip();
      }, options);
      document.addEventListener('pointerdown', event => {
        this.pointerType = event.pointerType;
        if (!this.tooltipButton?.contains(event.target) && !this.tooltip?.contains(event.target)) this.closeTooltip();
      }, options);
      window.addEventListener('resize', () => this.positionTooltip(), options);

      if (this.looks) return;
      // Fetch only when the section approaches the viewport. The source stays
      // inert until a look is selected, so hidden looks don't load product images.
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        // MARK:- Keep source-page work outside the initial page-load window.
        if (document.readyState === 'complete') this.load();
        else window.addEventListener('load', () => this.load(), {once: true, signal: this.controller.signal});
      }, {rootMargin: '400px'});
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.closeTooltip();
      this.controller.abort();
      this.observer?.disconnect();
    }

    async load() {
      const signal = this.controller.signal;
      this.status.hidden = false;
      this.panels.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.sourceUrl, location.origin);
      try {
        if (!this.dataset.sourceUrl) throw new Error('Choose a source page in the theme editor.');
        // MARK:- Share source fragments and cache context with product-page looks.
        const entries = await theme.tpmLookSource.get(url, {
          cacheContext: this.dataset.cacheContext, signal
        });
        const source = document.createElement('template');
        source.innerHTML = entries.map(entry => entry.html).join('');
        if (signal.aborted) return;

        this.looks = [...source.content.querySelectorAll('[data-tpm-look][data-look-image]')]
          .filter(look => look.querySelector('[data-hotspot-product-id]') && look.querySelector('hot-spots .hotspots img') && look.querySelector('hot-spots [data-block-container]'))
          .slice(0, Number(this.dataset.maxLooks) || 20);
        if (!this.looks.length) throw new Error('No available looks');

        this.looks.forEach((look, index) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'tpm-shop-the-look__thumbnail';
          tab.id = `${this.id}-Tab-${index}`;
          tab.dataset.tpmLookIndex = index;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', `${this.id}-Panel-${index}`);
          tab.setAttribute('aria-selected', 'false');
          tab.setAttribute('aria-label', `${this.dataset.lookLabel} ${index + 1}: ${look.dataset.lookTitle}`);
          tab.tabIndex = -1;
          const image = document.createElement('img');
          const imageUrl = new URL(look.dataset.lookImage, location.origin);
          imageUrl.searchParams.set('width', '480');
          image.src = imageUrl.href;
          image.alt = '';
          image.width = 240;
          image.height = 240;
          image.loading = 'lazy';
          const label = document.createElement('span');
          label.className = 'tpm-shop-the-look__thumbnail-title';
          label.textContent = look.dataset.lookTitle || this.dataset.lookLabel;
          const frame = document.createElement('span');
          frame.className = 'tpm-shop-the-look__thumbnail-frame';
          frame.append(image);
          if (this.dataset.showNumbers === 'true') {
            const number = document.createElement('span');
            number.className = 'tpm-shop-the-look__thumbnail-number';
            number.textContent = String(index + 1).padStart(2, '0');
            number.setAttribute('aria-hidden', 'true');
            frame.append(number);
          }
          tab.append(frame, label);
          this.tabs.append(tab);

          const panel = document.createElement('div');
          panel.id = `${this.id}-Panel-${index}`;
          panel.className = 'tpm-shop-the-look__panel hotspots-section';
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
          panel.hidden = true;
          this.panels.append(panel);
        });

        this.status.hidden = true;
        this.querySelector('[data-tpm-look-navigation]').hidden = false;
        this.querySelector('[data-tpm-look-count]').textContent = this.dataset.countLabel.replace('[count]', this.looks.length);
        const initial = this.looks.findIndex(look => look.dataset.lookTitle === this.dataset.defaultLook);
        this.selectLook(Math.max(0, initial));
      } catch (error) {
        if (signal.aborted) return;
        this.looks = null;
        this.status.textContent = this.dataset.emptyMessage;
        const fallback = this.querySelector('[data-tpm-look-fallback]');
        if (fallback) fallback.hidden = false;
        // Keep the server-rendered link available when the source can't load.
        console.warn(url.href, error);
      } finally {
        if (!signal.aborted) this.panels.removeAttribute('aria-busy');
      }
    }

    mountLook(index) {
      const panel = this.panels.children[index];
      if (!panel || !this.looks?.[index]) return false;
      if (panel.childElementCount) return true;
      const look = this.looks[index].cloneNode(true);
      look.querySelectorAll('script, [data-shopify-editor-block], [data-aos]').forEach(element => {
        if (element.tagName === 'SCRIPT') element.remove();
        else {
          element.removeAttribute('data-shopify-editor-block');
          element.removeAttribute('data-aos');
        }
      });
      look.querySelectorAll('[data-button]:not([data-hotspot-product-id])').forEach(button => {
        look.querySelector(`[data-hotspot-block="${button.dataset.button}"]`)?.remove();
        button.remove();
      });
      // The compact list keeps a single thumbnail per product. Variant choices
      // stay on the product page; hover images would obscure these small cards.
      look.querySelectorAll('.grid-product__colors, .grid-product__color-image, .grid-product__secondary-image, .tpm-stickers').forEach(element => element.remove());

      // Keep the source's markup, coordinates, product cards and cart controls.
      // The homepage shows all products, so it owns selection instead of the
      // source component's single-card toggling and document-level listeners.
      // MARK:- Skip malformed source looks before mounting or changing selection.
      const hotspots = look.querySelector('hot-spots');
      if (!hotspots?.querySelector('[data-block-container]') || !hotspots.querySelector('.hotspots')) return false;
      const layout = document.createElement('div');
      layout.className = 'tpm-shop-the-look__layout';
      layout.append(...hotspots.childNodes);
      hotspots.replaceWith(layout);
      const products = layout.querySelector('[data-block-container]');
      const introduction = document.createElement('div');
      introduction.className = 'tpm-shop-the-look__look-intro';
      if (this.dataset.featuredLabel) {
        const badge = document.createElement('p');
        badge.className = 'tpm-shop-the-look__badge';
        const star = document.createElement('span');
        star.textContent = '★ ';
        star.setAttribute('aria-hidden', 'true');
        badge.append(star, this.dataset.featuredLabel);
        introduction.append(badge);
      }
      const heading = document.createElement('h3');
      heading.className = 'tpm-shop-the-look__title';
      heading.textContent = look.dataset.lookTitle || this.dataset.lookLabel;
      introduction.append(heading);
      if (look.dataset.lookDescription) {
        const description = document.createElement('p');
        description.className = 'tpm-shop-the-look__description';
        description.textContent = look.dataset.lookDescription;
        introduction.append(description);
      }
      look.querySelector('.hotspots__title')?.parentElement.remove();
      products.prepend(introduction);
      const photoContainer = layout.querySelector('.hotspots');
      if (this.dataset.showNumbers === 'true') {
        const number = document.createElement('span');
        number.className = 'tpm-shop-the-look__photo-number';
        number.textContent = `${this.dataset.lookLabel} ${String(index + 1).padStart(2, '0')}`;
        photoContainer.append(number);
      }
      const actions = document.createElement('div');
      actions.className = 'tpm-shop-the-look__actions';
      const addAll = look.querySelector('.quick-add-all');
      if (addAll) {
        addAll.className = 'tpm-shop-the-look__button tpm-shop-the-look__add-all';
        addAll.removeAttribute('style');
        const count = Number(addAll.dataset.productCount);
        const label = document.createElement('span');
        label.textContent = (count === 1 ? this.dataset.addOneLabel : this.dataset.addManyLabel).replace('[count]', count);
        addAll.replaceChildren(label);
        if (addAll.dataset.addAllTotal) {
          const total = document.createElement('span');
          total.className = 'tpm-shop-the-look__total';
          total.textContent = addAll.dataset.addAllTotal;
          addAll.append(total);
        }
        actions.append(addAll);
      }
      look.querySelector('.hotspots__add-all')?.remove();
      if (this.dataset.browseLabel) {
        const browse = document.createElement('a');
        browse.className = 'tpm-shop-the-look__button tpm-shop-the-look__browse';
        browse.href = this.dataset.sourceUrl;
        browse.append(this.dataset.browseLabel);
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = ' →';
        browse.append(arrow);
        actions.append(browse);
      }
      products.append(actions);
      const cartError = document.createElement('p');
      cartError.className = 'tpm-shop-the-look__cart-error';
      cartError.setAttribute('role', 'alert');
      cartError.hidden = true;
      products.append(cartError);

      // The compact plus action adds single-variant products directly. Products
      // requiring a choice keep their existing link to the product page.
      look.querySelectorAll('.modal--quick-shop, .quick-product__btn').forEach(element => element.remove());

      layout.querySelectorAll('[data-hotspot-block]').forEach((block, productIndex) => {
        const button = layout.querySelector(`[data-button="${block.dataset.hotspotBlock}"]`);
        block.id = `${panel.id}-Product-${productIndex}`;
        block.classList.remove('is-active');
        if (button) {
          button.setAttribute('aria-controls', block.id);
          button.setAttribute('aria-pressed', 'false');
          if (this.dataset.hotspotAppearance === 'outline') button.replaceChildren(this.plusIcon());
        }
        const card = block.querySelector('.grid__item-image-wrapper');
        // MARK:- Skip incomplete cards and their hotspot instead of dereferencing missing markup.
        const cardTitle = card?.querySelector('.grid-product__title');
        const cardMeta = card?.querySelector('.grid-product__meta');
        if (!card || !cardTitle || !cardMeta) {
          button?.remove();
          block.remove();
          return;
        }
        const price = card.querySelector('.grid-product__price');
        if (price) card.append(price);
        if (button) {
          const tooltip = document.createElement('div');
          tooltip.id = `${block.id}-Tooltip`;
          tooltip.className = 'tpm-shop-the-look__tooltip';
          tooltip.setAttribute('role', 'tooltip');
          tooltip.hidden = true;
          const title = document.createElement('p');
          title.className = 'tpm-shop-the-look__tooltip-title';
          title.textContent = cardTitle.textContent.trim();
          tooltip.append(title);
          if (price) {
            const tooltipPrice = price.cloneNode(true);
            tooltipPrice.className = 'tpm-shop-the-look__tooltip-price';
            tooltip.append(tooltipPrice);
          }
          button.setAttribute('aria-describedby', tooltip.id);
          photoContainer.append(tooltip);
        }
        if (block.dataset.lookProductDetail) {
          const detail = document.createElement('div');
          detail.className = 'tpm-shop-the-look__product-detail';
          detail.textContent = block.dataset.lookProductDetail;
          cardMeta.append(detail);
        }
        const action = card.querySelector('.icon-container');
        if (action) {
          const isAdd = action.matches('button');
          if (isAdd) action.type = 'button';
          action.className = isAdd ? 'tpm-shop-the-look__product-action tpm-shop-the-look__add-product' : 'tpm-shop-the-look__product-action';
          const label = isAdd ? this.dataset.addLabel : this.dataset.optionsLabel;
          const productTitle = cardTitle.textContent.trim();
          action.setAttribute('aria-label', `${label}: ${productTitle}`);
          action.title = label;
          action.replaceChildren(this.plusIcon());
          card.append(action);
        }
        card.querySelectorAll('img').forEach(image => { image.sizes = '64px'; });
      });
      const photo = layout.querySelector('.hotspots img');
      if (photo) {
        photo.sizes = '(min-width: 769px) 50vw, 100vw';
        photo.alt = look.dataset.lookTitle || this.dataset.lookLabel;
      }
      panel.append(look);
      return true;
    }

    selectLook(index, moveFocus = false, announce = false) {
      if (!this.looks || index < 0 || index >= this.looks.length) return;
      this.closeTooltip();
      if (!this.mountLook(index)) return;
      this.selectedIndex = index;
      [...this.panels.children].forEach((panel, position) => { panel.hidden = position !== index; });
      [...this.tabs.children].forEach((tab, position) => {
        tab.setAttribute('aria-selected', String(position === index));
        tab.tabIndex = position === index ? 0 : -1;
      });
      const tab = this.tabs.children[index];
      if (moveFocus) tab.focus({preventScroll: true});
      // Scroll only the thumbnail strip, never jump the whole page on selection.
      this.tabs.scrollTo({left: tab.offsetLeft - (this.tabs.clientWidth - tab.clientWidth) / 2, behavior: this.scrollBehavior});
      // MARK:- Keep focus on an arrow at the first/last look; selectLook guards boundaries.
      this.querySelector('[data-tpm-look-previous]').setAttribute('aria-disabled', String(index === 0));
      this.querySelector('[data-tpm-look-next]').setAttribute('aria-disabled', String(index === this.looks.length - 1));
      // MARK:- Arrow navigation keeps focus and announces the newly selected look.
      this.announcement.textContent = announce
        ? this.dataset.lookSelectedMessage.replace('[index]', index + 1).replace('[count]', this.looks.length).replace('[title]', this.looks[index].dataset.lookTitle)
        : '';
    }

    selectProduct(button) {
      const panel = button.closest('.tpm-shop-the-look__panel');
      const block = panel.querySelector(`[data-hotspot-block="${button.dataset.button}"]`);
      if (!block) return;
      panel.querySelectorAll('[data-hotspot-block]').forEach(item => item.classList.toggle('is-selected', item === block));
      panel.querySelectorAll('[data-button]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      this.announcement.textContent = this.dataset.selectedMessage.replace('[product]', button.getAttribute('aria-label'));
      // Keep the photo and tooltip in view, including on the stacked mobile
      // layout. The corresponding product remains highlighted below it.
    }

    onTooltipPointer(event) {
      if (event.pointerType === 'touch') return;
      const target = event.target.closest('[data-hotspot-product-id], .tpm-shop-the-look__tooltip');
      if (!target || target.contains(event.relatedTarget)) return;
      if (event.type === 'pointerover') {
        clearTimeout(this.tooltipTimer);
        if (target.matches('[data-hotspot-product-id]')) this.openTooltip(target);
      } else {
        // Allow the pointer to cross the small gap into the tooltip itself.
        this.tooltipTimer = setTimeout(() => {
          if (!this.tooltipButton?.matches(':focus-visible')) this.closeTooltip();
        }, 180);
      }
    }

    openTooltip(button) {
      this.closeTooltip();
      const tooltip = document.getElementById(button.getAttribute('aria-describedby'));
      if (!tooltip) return;
      this.tooltipButton = button;
      this.tooltip = tooltip;
      button.classList.add('is-tooltip-open');
      tooltip.hidden = false;
      this.positionTooltip();
    }

    closeTooltip() {
      clearTimeout(this.tooltipTimer);
      this.tooltipButton?.classList.remove('is-tooltip-open');
      if (this.tooltip) this.tooltip.hidden = true;
      this.tooltip = null;
      this.tooltipButton = null;
    }

    positionTooltip() {
      if (!this.tooltip || !this.tooltipButton) return;
      const photo = this.tooltip.parentElement.getBoundingClientRect();
      const button = this.tooltipButton.getBoundingClientRect();
      const {offsetWidth: width, offsetHeight: height} = this.tooltip;
      const inset = 12;
      const left = Math.max(inset, Math.min(button.left - photo.left + (button.width - width) / 2, photo.width - width - inset));
      const below = button.bottom - photo.top + inset;
      const above = button.top - photo.top - height - inset;
      const top = below + height <= photo.height - inset ? below : Math.max(inset, above);
      this.tooltip.style.left = `${left}px`;
      this.tooltip.style.top = `${top}px`;
    }

    get scrollBehavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.dataset.disableAnimations === 'true' ? 'auto' : 'smooth';
    }

    onClick(event) {
      const tab = event.target.closest('[data-tpm-look-index]');
      if (tab) this.selectLook(Number(tab.dataset.tpmLookIndex));
      else if (event.target.closest('[data-tpm-look-previous]')) this.selectLook(this.selectedIndex - 1, false, true);
      else if (event.target.closest('[data-tpm-look-next]')) this.selectLook(this.selectedIndex + 1, false, true);
      else if (event.target.closest('.tpm-shop-the-look__add-product, .tpm-shop-the-look__add-all')) {
        event.preventDefault();
        this.addToCart(event.target.closest('button'));
      }
      else {
        const button = event.target.closest('[data-hotspot-product-id]');
        if (button) {
          this.selectProduct(button);
          if (this.pointerType === 'touch' && this.tooltipButton === button) this.closeTooltip();
          else this.openTooltip(button);
        }
      }
    }

    onKeydown(event) {
      this.pointerType = 'keyboard';
      if (event.key === 'Escape' && this.tooltip) {
        // Handle this dismissal before the theme's global search/modal handlers
        // so keyboard focus stays on the hotspot.
        event.preventDefault();
        event.stopPropagation();
        this.closeTooltip();
        return;
      }
      if (!event.target.closest('[role="tab"]')) return;
      const directions = {ArrowRight: 1, ArrowLeft: -1};
      let index;
      if (event.key in directions) index = (this.selectedIndex + directions[event.key] + this.looks.length) % this.looks.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.looks.length - 1;
      else return;
      event.preventDefault();
      this.selectLook(index, true);
    }

    plusIcon() {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      for (const coordinates of [[5, 12, 19, 12], [12, 5, 12, 19]]) {
        const line = document.createElementNS(svg.namespaceURI, 'line');
        ['x1', 'y1', 'x2', 'y2'].forEach((key, index) => line.setAttribute(key, coordinates[index]));
        line.setAttribute('stroke', 'currentColor');
        line.setAttribute('stroke-width', '2');
        svg.append(line);
      }
      return svg;
    }

    async addToCart(button) {
      if (!button || button.disabled) return;
      const all = button.classList.contains('tpm-shop-the-look__add-all');
      const ids = all ? button.dataset.addAllVars.split(',') : [button.dataset.variantId];
      const properties = all
        ? {'_quick_shop': 'Shop the Look [Add All]', 'Part of Look': button.dataset.shopTheLook}
        : {'_quick_shop': 'Shop the Look', 'Part of': button.dataset.shopTheLook};
      // MARK:- A detached or incomplete source control must not start a cart request.
      const content = button.closest('.hotspots__content');
      if (!content) return;
      const errorMessage = content.querySelector('.tpm-shop-the-look__cart-error');
      const cart = await theme.tpmCart.add({
        button,
        errorElement: errorMessage,
        isMultipleAdd: all,
        items: ids.map(id => ({id: Number(id), quantity: 1, properties}))
      });
      if (cart) this.announcement.textContent = this.dataset.addedLabel;
      else if (errorMessage && !errorMessage.hidden) errorMessage.scrollIntoView({block: 'nearest', behavior: this.scrollBehavior});
    }
  });
}
